// src/components/StalenessTag.jsx
// Inline staleness warning tag for feed cards, alert cards, and station cards.
//
// Two visual states:
//   aging — amber ⚠ "Xh old" with tooltip — conditions may be changing
//   stale — grey  ⏱ "Xh old" with strikethrough opacity — likely outdated
//
// Used on:
//   FeedCard      — community reports (flood, road, power, SOS)
//   AlertCard     — NWS weather alerts that are hours old
//   StationCard   — gas station status reports
//   ShelterCard   — shelter availability

import { useLangStore } from '@store/lang';
import { getStaleness, getStalenessLabel } from '@services/staleness';
import { T } from '@components/theme';

export function StalenessTag({ type, tsMs, compact = false }) {
  const level = getStaleness(type, tsMs);
  if (!level) return null;

  const ageMin   = tsMs && !isNaN(tsMs) ? Math.round((Date.now() - tsMs) / 60_000) : null;
  const ageLabel = ageMin < 60
    ? `${ageMin}m old`
    : `${Math.round(ageMin / 60)}h old`;
  const fullLabel = getStalenessLabel(type, tsMs);

  const isStale = level === 'stale';

  return (
    <span
      title={fullLabel}
      aria-label={fullLabel}
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        gap:             3,
        fontSize:        compact ? 8 : 9,
        fontWeight:      700,
        padding:        '1px 5px',
        borderRadius:    4,
        background:      isStale ? 'rgba(107,114,128,0.12)' : 'rgba(217,119,6,0.12)',
        border:         `1px solid ${isStale ? 'rgba(107,114,128,0.25)' : 'rgba(217,119,6,0.3)'}`,
        color:           isStale ? T.dim : '#92400e',
        flexShrink:      0,
        letterSpacing:   0.2,
        whiteSpace:     'nowrap',
      }}
    >
      <span style={{ fontSize: compact ? 8 : 9 }}>
        {isStale ? '⏱' : '⚠'}
      </span>
      {ageLabel}
    </span>
  );
}

// Banner variant — wider, used at the top of sections with stale cached data
export function StalenessBanner({ type, tsMs, source }) {
  const { lang } = useLangStore();
  const level = getStaleness(type, tsMs);
  if (!level) return null;

  const ageMin   = tsMs && !isNaN(tsMs) ? Math.round((Date.now() - tsMs) / 60_000) : null;
  const ageLabel = ageMin < 60
    ? `${ageMin} minutes`
    : ageMin < 1440
      ? `${Math.round(ageMin / 60)} hour${Math.round(ageMin / 60) !== 1 ? 's' : ''}`
      : `${Math.round(ageMin / 1440)} day${Math.round(ageMin / 1440) !== 1 ? 's' : ''}`;

  const isStale = level === 'stale';

  return (
    <div style={{
      margin:       '6px 14px 2px',
      padding:      '7px 10px',
      borderRadius:  8,
      background:    isStale ? 'rgba(107,114,128,0.08)' : 'rgba(217,119,6,0.08)',
      border:       `1px solid ${isStale ? 'rgba(107,114,128,0.2)' : 'rgba(217,119,6,0.25)'}`,
      display:      'flex',
      alignItems:   'center',
      gap:           6,
    }}>
      <span style={{ fontSize: 13, flexShrink: 0 }}>
        {isStale ? '⏱' : '⚠️'}
      </span>
      <div style={{
        fontSize:    10,
        color:       isStale ? T.dim : '#92400e',
        fontWeight:   600,
        lineHeight:   1.4,
      }}>
        {source ? `${source} data is ` : 'Data is '}{ageLabel} old.
        {isStale
          ? ' Conditions may have changed significantly — verify with official sources.' 
          : (lang==='es'?' Verifique condiciones actuales antes de actuar.':' Verify current conditions before acting.')}
      </div>
    </div>
  );
}
